import styles from "./index.module.scss";
import { Button, CircularProgress, Stack, TablePagination } from "@mui/material";
import { useEffect, useState } from "react";
import { SearchFilter } from "./SearchFilter/SearchFilter";
import CompanyInvoice from "./CompanyInvoice/CompanyInvoice";
import { DeliveryOption, FilterOptions, WrappingSizeOption } from "../../../libs/types";
import { getAllBrandList, getBrandInvoices, getBrandList, getBrandlistWithFilters, getUserOptions } from "../../../apis/brand-api";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import ProcessFeeDetailsDialog from "../../dialogs/invoiceDetails/ProcessFeeDetailsDialog";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../apis/store";
import { ProcessFeeDetailsResponseData, ProcessFeeOption } from "../../../apis/models/ProcessFeeDetailsResponse";
import { ProcessFeeOptionRequestItem, ProcessFeeRequestItem } from "../../../apis/models/ProcessFeeRequest";
import { getDeliveryOptions, getOfficeWorkFee, getOfficeWorkFeeOptions, getProcessFeeDetails, getProcessFeeOptions, getSizeOptions, getWrappingFee, postOfficeWorkFee, postProcessFee, postProcessFeeOptions, postWrappingFee } from "../../../apis/invoice-api";
import { InvoiceListResponseData, InvoiceMasterPUTRequest } from "../../../apis/models/InvoiceMasterRequest";
import { callDownloadCurrentInvoicesApi, getInvoiceDetails, putInvoiceMaster } from "../../../apis/invmaster-api";
import { BrandlistFilterRequest } from "../../../apis/models/BrandlistRequest";
import { saveBrandOptions, saveUserOptions } from "../../../apis/reducers/optionReducer";
import UploadCsvDialog from "../../dialogs/brandList/UploadShippingFeeDialog/UploadCsvDialog";
import BatchDownloadDialog from "../../dialogs/brandList/BatchDownloadDialog";
import { InvStatus, LOCALSTORAGE } from "../../../libs/constants";
import { OfficeWorkOption, OfficeWorkResponseData } from "../../../apis/models/OfficeWorkResponse";
import { error, success } from "../../../apis/reducers/snackbarsReducer";
import OfficeWorkDialog, { OfficeWorkDialogData } from "../../dialogs/invoiceDetails/OfficeWorkDialog";
import { str2number } from "../../../libs/helpers";
import { OfficeWorkRequest } from "../../../apis/models/OfficeWorkRequest";
import WrappingFeeDialog, { WrappingFeeAddDialogData } from "../../dialogs/invoiceDetails/WrappingFeeDialog";
import { WrappingFeeResponseData } from "../../../apis/models/WrappingFeeResponse";
import { WrappingFeeRequestItem } from "../../../apis/models/WrappingFeeRequest";
import DateWithoutTime from "../../../libs/dateWithoutTime";

export const BrandList = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageLimit, setPageLimit] = useState<number>(50);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    brandText: "",
    contactPerson: "",
    day: "",
    month: "",
    invStatus: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingChangeStatus, setIsLoadingChangeStatus] = useState<boolean>(false);
  const [brandList, setBrandlist] = useState<BrandlistResponseData[]>([]);
  const [processFeeState, setProcessFeeState] = useState<{
    brand?: BrandlistResponseData,
    invoices: InvoiceListResponseData[],
    openDialog: boolean,
    options: ProcessFeeOption[],
  }>({
    brand: undefined,
    invoices: [],
    openDialog: false,
    options: [],
  });
  const [officeWorkFeeState, setOfficeWorkFeeState] = useState<{
    brand?: BrandlistResponseData,
    invoices: InvoiceListResponseData[],
    openDialog: boolean,
    options: OfficeWorkOption[],
    dateArray: DateWithoutTime[],
  }>({
    brand: undefined,
    invoices: [],
    openDialog: false,
    options: [],
    dateArray: [],
  });
  const [wrappingFeeState, setWrappingFeeState] = useState<{
    brand?: BrandlistResponseData,
    invoices: InvoiceListResponseData[],
    openDialog: boolean,
    sizeOptions: WrappingSizeOption[],
    deliveryOptions: DeliveryOption[],
    dateArray: DateWithoutTime[],
  }>({
    brand: undefined,
    invoices: [],
    openDialog: false,
    sizeOptions: [],
    deliveryOptions: [],
    dateArray: [],
  });
  const [openUploadFreightCost, setOpenUploadFreightCost] = useState<boolean>(false);
  const [openBatchDownload, setOpenBatchDownload] = useState<boolean>(false);

  const getBrands = (isChangingPageLimit: boolean = false, limit?: number) => {
    setLoading(true);
    const currentPage = isChangingPageLimit ? 1 : page+1;
    const currentPageLimit = isChangingPageLimit && limit !== undefined ? limit : pageLimit;
    getBrandList(currentPage, currentPageLimit)
      .then((res) => {
        setBrandlist(res.data.data);
        setTotalItems(res.data.items);
      })
      .catch((err) => setBrandlist([]))
      .finally(() => setLoading(false))
  }

  const handleChangePageLimit = (limit: number) => {
    setPageLimit(limit)
    if (filterOptions.brandText !== "" || filterOptions.contactPerson !== "" || filterOptions.day !== "" || filterOptions.month !== "" || filterOptions.invStatus.length > 0) {
      handleChangeFilterOption(true, limit)
    } else {
      getBrands(true, limit);
    }
  }

  const paginationRender = (p: number) => {
    const fromItem = p === 1 ? 1 : ((pageLimit *(p - 1)) + 1);
    let toItem;
    if (p*pageLimit <= totalItems) {
      toItem = p* pageLimit;
    } else {
      toItem = totalItems
    }
    return <span><span style={{marginRight: "16px"}}>{totalItems}件中</span>{fromItem}～{toItem}件</span>;
  }

  const changeInvStatus = (id: string, inv: InvoiceMasterPUTRequest) => {
    setIsLoadingChangeStatus(true)
    putInvoiceMaster(id, inv)
      .then((res) => {
        handleChangeFilterOption();
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoadingChangeStatus(false))
  }

  const handleChangeFilterOption = (isChangingPageLimit: boolean = false, limit?: number) => {
    setLoading(true)
    const req: BrandlistFilterRequest = {
      brandNameOrNo: filterOptions.brandText ? filterOptions.brandText : undefined,
      cutoffDate: Number(filterOptions.day),
      billingDate: filterOptions.month,
      invoiceStatus: filterOptions.invStatus.length > 0 ? filterOptions.invStatus : undefined,
      salesStaffId: !isNaN(parseInt(filterOptions.contactPerson)) && parseInt(filterOptions.contactPerson) !== 0 ? parseInt(filterOptions.contactPerson) : undefined
    }
    const currentPage = isChangingPageLimit ? 1 : page+1;
    const currentPageLimit = isChangingPageLimit && limit !== undefined ? limit : pageLimit;
    getBrandlistWithFilters(currentPage, currentPageLimit, req)
      .then(res => {
        setBrandlist(res.data.data);
        setTotalItems(res.data.items);
      })
      .catch(err => console.log(err))
      .finally(() => setLoading(false))
  }

  const handleOpenProcessFee = async (brand: BrandlistResponseData) => {
    const invoices = await getInvoices(brand);
    const unConfirmedInvoices = invoices.filter(i => i.status === InvStatus.NOT_CONFIRMED)
    if (unConfirmedInvoices.length <= 0) {
      return
    }

    try {
      const options = await getInvoiceProcessFeeOptions(unConfirmedInvoices[0].invoiceMasterId);
      setProcessFeeState({
        brand: brand,
        openDialog: true,
        options: options,
        invoices: unConfirmedInvoices
      })
    } catch (error) {
      // nothing
    }
  }

  const addProcessFeeOption = async (addItem: ProcessFeeOptionRequestItem) => {
    if (!processFeeState.options.every(item => item.processFeeOption.workDetails !== addItem.workDetails)) {
      return
    }
    const reqItems = processFeeState.options.map(o => {
      const option: ProcessFeeOptionRequestItem = {
        id: o.processFeeOptionId,
        unitPrice: o.processFeeOption.unitPrice,
        workDetails: o.processFeeOption.workDetails,
        order: o.order!,
      }
      return option;
    })
    addItem.order = processFeeState.options.reduce((prev, o) => Math.max(prev, o.order ?? 0), 0) + 1;
    reqItems.push(addItem);
    
    const invoiceMasterId = processFeeState.invoices[0].invoiceMasterId;
    await postProcessFeeOptions(invoiceMasterId, {processFeeOptions: reqItems})
    const options = await getInvoiceProcessFeeOptions(invoiceMasterId);
    setProcessFeeState({...processFeeState, options: options })
  }

  async function getInvoiceProcessFeeOptions(invoiceMasterId: string): Promise<ProcessFeeOption[]> {
    try {
      const response = await getProcessFeeOptions(invoiceMasterId);
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  const addProcessFeeDetails = async (items: ProcessFeeDetailsResponseData[]) => {
    const updatePromises = [];
    for (let i = 0; i < processFeeState.invoices.length; i++) {
      const invoice = processFeeState.invoices[i]
      const from = new Date(invoice.from)
      const to = new Date(invoice.to)

      const addData = items.filter(f => {
        const date = new Date(f.dateAdded)
        return from.getTime() <= date.getTime() && date.getTime() <= to.getTime()
      }).map(item => {
        const data: ProcessFeeRequestItem ={
          dateAdded: new DateWithoutTime(item.dateAdded).getValue4Server(),
          invoiceMasterId: item.invoiceMasterId,
          processFeeOptionId: item.processFeeOptionId,
          productCode: item.productNumber,
          quantity: item.quantity
        }
        return data
      })

      if (addData.length <= 0) {
        continue;
      }

      const serverDatas = await getInvoiceProcessFeeDetails(invoice.invoiceMasterId)
      const req = addData.concat(serverDatas.map(item => {
        const data: ProcessFeeRequestItem ={
          id: item.processFeeId,
          dateAdded: item.dateAdded,
          invoiceMasterId: item.invoiceMasterId,
          processFeeOptionId: item.processFeeOptionId,
          productCode: item.productNumber,
          quantity: item.quantity
        }
        return data
      }))

      updatePromises.push(postProcessFee(invoice.invoiceMasterId, req))
    }

    if (updatePromises.length > 0) {
      try {
        await Promise.all(updatePromises)
        dispatch(success("保存しました"));
      } catch (e) {
        dispatch(error("保存に失敗しました"))
      }
    }
    closeProcessFeeDialog()
  }

  function closeProcessFeeDialog() {
    setProcessFeeState({
      brand: undefined,
      openDialog: false,
      options: [],
      invoices: []
    })
  }

  const handleOpenOfficeWorkFee = async (brand: BrandlistResponseData) => {
    const invoices = await getInvoices(brand);
    const unConfirmedInvoices = invoices.filter(i => i.status === InvStatus.NOT_CONFIRMED)
    if (unConfirmedInvoices.length <= 0) {
      return
    }

    try {
      const promises = [getOfficeWorkFeeOptions(unConfirmedInvoices[0].invoiceMasterId)]
      unConfirmedInvoices.forEach(invoice => {
        promises.push(getOfficeWorkFee(invoice.invoiceMasterId))
      })
      const values = await Promise.all(promises)
      const officeWorkDataList: OfficeWorkResponseData[] = []
      for (let i = 1; i < values.length; i++) {
        values[i].data.data.forEach((d: OfficeWorkResponseData) => {
          officeWorkDataList.push(d)
        })
      }
      const existDates: DateWithoutTime[] = [];
      officeWorkDataList.forEach(data => {
        if (existDates.findIndex(ed => ed.equals(new DateWithoutTime(data.dateAdded))) >= 0) {
          return
        }
        existDates.push(new DateWithoutTime(data.dateAdded))
      });
      setOfficeWorkFeeState({
        brand: brand,
        invoices: unConfirmedInvoices,
        openDialog: true,
        options: values[0].data.data,
        dateArray: existDates,
      })
    } catch (error) {
      // nothing
    }
  }

  const saveOfficeWorkFee = async (newData: OfficeWorkDialogData[]) => {
    const updatePromises = [];
    for (let i = 0; i < officeWorkFeeState.invoices.length; i++) {
      const invoice = officeWorkFeeState.invoices[i]
      const from = new Date(invoice.from)
      const to = new Date(invoice.to)

      const fees = await getOfficeWorkFee(invoice.invoiceMasterId)
      const req: OfficeWorkRequest[] = fees.data.data.map((fee: OfficeWorkResponseData) => {
        const reqItem: OfficeWorkRequest = {
          dateAdded: new DateWithoutTime(fee.dateAdded).getValue4Server(),
          memo: fee.memo,
          invoiceMasterId: invoice.invoiceMasterId,
          officeWorkFeeOptionId: fee.officeWorkFeeOptionId,
          quantity: fee.quantity,
        }
        return reqItem
      });

      newData.forEach(item => {
        item.officeWorkFees.forEach(itemData => {
          const date = item.dateAdded.getDate()!
          if (from.getTime() <= date.getTime() && date.getTime() <= to.getTime()) {
            const reqItem: OfficeWorkRequest = {
              dateAdded: item.dateAdded.getValue4Server(),
              memo: item.remarks,
              invoiceMasterId: invoice.invoiceMasterId,
              officeWorkFeeOptionId: itemData.officeWorkFeeOptionId,
              quantity: str2number(itemData.quantity),
            }
            req.push(reqItem);
          }
        })
      })

      if (req.length <= 0) {
        continue;
      }
      updatePromises.push(postOfficeWorkFee(invoice.invoiceMasterId, req))
    }

    if (updatePromises.length > 0) {
      try {
        await Promise.all(updatePromises)
        dispatch(success("保存しました"));
      } catch (e) {
        dispatch(error("保存に失敗しました"))
      }
    }
    closeOfficeWorkFeeDialog()
  }

  function closeOfficeWorkFeeDialog() {
    setOfficeWorkFeeState({
      brand: undefined,
      invoices: [],
      openDialog: false,
      options: [],
      dateArray: [],
    })
  }

  const handleOpenWrappingFee = async (brand: BrandlistResponseData) => {
    const invoices = await getInvoices(brand);
    const unConfirmedInvoices = invoices.filter(i => i.status === InvStatus.NOT_CONFIRMED)
    if (unConfirmedInvoices.length <= 0) {
      return
    }

    const invoiceMasterId = unConfirmedInvoices[0].invoiceMasterId
    try {
      const promises = [getDeliveryOptions(invoiceMasterId), getSizeOptions(invoiceMasterId)]
      unConfirmedInvoices.forEach(invoice => {
        promises.push(getWrappingFee(invoice.invoiceMasterId))
      })
      const values = await Promise.all(promises)
      const wrappingDataList: WrappingFeeResponseData[] = []
      for (let i = 2; i < values.length; i++) {
        values[i].data.data.forEach((d: WrappingFeeResponseData) => {
          wrappingDataList.push(d)
        })
      }
      const existDates: DateWithoutTime[] = [];
      wrappingDataList.forEach(data => {
        if (existDates.findIndex(ed => ed.equals(new DateWithoutTime(data.dateAdded))) >= 0) {
          return
        }
        existDates.push(new DateWithoutTime(data.dateAdded))
      });
      setWrappingFeeState({
        brand: brand,
        invoices: unConfirmedInvoices,
        openDialog: true,
        sizeOptions: values[1].data.data,
        deliveryOptions: values[0].data.data,
        dateArray: existDates,
      })
    } catch (error) {
      // nothing
    }
  }

  const saveWrappingFee = async (addData: WrappingFeeAddDialogData[]) => {
    const updatePromises = [];
    for (let i = 0; i < wrappingFeeState.invoices.length; i++) {
      const invoice = wrappingFeeState.invoices[i]
      const from = new Date(invoice.from)
      const to = new Date(invoice.to)

      const fees = await getWrappingFee(invoice.invoiceMasterId)
      const req: WrappingFeeRequestItem[] = fees.data.data.map((fee: WrappingFeeResponseData) => {
        const reqItem: WrappingFeeRequestItem = {
          dateAdded: new DateWithoutTime(fee.dateAdded).getValue4Server(),
          memo: fee.memo,
          invoiceMasterId: invoice.invoiceMasterId,
          differenceInSalesNumber: fee.differenceInSalesNumber,
          deliveryMethodOptionId: fee.deliveryMethodOptionId ?? undefined,
          wrappingSizeOptionId: fee.wrappingSizeOptionId ?? undefined,
          quantity: fee.quantity,
        }
        return reqItem
      });

      addData.forEach(item => {
        item.sizeList.forEach(itemData => {
          const date = item.dateAdded.getDate()!
          if (from.getTime() <= date.getTime() && date.getTime() <= to.getTime()) {
            const reqItem: WrappingFeeRequestItem = {
              dateAdded: item.dateAdded.getValue4Server(),
              memo: item.remarks,
              invoiceMasterId: invoice.invoiceMasterId,
              wrappingSizeOptionId: itemData.wrappingSizeOptionId ?? undefined,
              quantity: str2number(itemData.quantity),
              differenceInSalesNumber: str2number(item.differenceInSalesNumber),
            }
            req.push(reqItem);
          }
        })

        item.deliveryList.forEach(itemData => {
          const date = item.dateAdded.getDate()!
          if (from.getTime() <= date.getTime() && date.getTime() <= to.getTime()) {
            const reqItem: WrappingFeeRequestItem = {
              dateAdded: item.dateAdded.getValue4Server(),
              memo: item.remarks,
              invoiceMasterId: invoice.invoiceMasterId,
              deliveryMethodOptionId: itemData.deliveryMethodOptionId ?? undefined,
              quantity: str2number(itemData.quantity),
              differenceInSalesNumber: str2number(item.differenceInSalesNumber),
            }
            req.push(reqItem);
          }
        })
      })

      if (req.length <= 0) {
        continue;
      }
      updatePromises.push(postWrappingFee(invoice.invoiceMasterId, req))
    }
    if (updatePromises.length > 0) {
      try {
        await Promise.all(updatePromises)
        dispatch(success("保存しました"));
      } catch (e) {
        dispatch(error("保存に失敗しました"))
      }
    }
    closeWrappingFeeDialog()
  }

  const downloadInvoice = async (pdf: boolean, excel: boolean) => {
    setOpenBatchDownload(false)

    const req: BrandlistFilterRequest = {
      brandNameOrNo: filterOptions.brandText ? filterOptions.brandText : undefined,
      cutoffDate: Number(filterOptions.day),
      billingDate: filterOptions.month,
      invoiceStatus: filterOptions.invStatus.length > 0 ? filterOptions.invStatus : undefined,
      salesStaffId: !isNaN(parseInt(filterOptions.contactPerson)) && parseInt(filterOptions.contactPerson) !== 0 ? parseInt(filterOptions.contactPerson) : undefined
    }

    const now = new Date()
    const response = await callDownloadCurrentInvoicesApi(pdf, excel, req)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `請求書_一括_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.zip`);
    document.body.appendChild(link);
    link.click();

    handleChangeFilterOption()
  }

  function closeWrappingFeeDialog() {
    setWrappingFeeState({
      brand: undefined,
      invoices: [],
      openDialog: false,
      sizeOptions: [],
      deliveryOptions: [],
      dateArray: [],
    })
  }

  async function getInvoices(brand: BrandlistResponseData): Promise<InvoiceListResponseData[]> {
    try {
      const response = await getBrandInvoices(brand.brandNo);
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  async function getInvoiceProcessFeeDetails(invoiceMasterId: string): Promise<ProcessFeeDetailsResponseData[]> {
    try {
      const response = await getProcessFeeDetails(invoiceMasterId);
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  useEffect(() => {
    handleChangeFilterOption();
  },[page])

  useEffect(() => {
    getUserOptions()
      .then(res => {
        dispatch(saveUserOptions(res.data.data))
      })
      .catch(err => console.log(err))
    getAllBrandList()
      .then(res => {
        const brandNameList = (res.data.data as BrandlistResponseData[]).map(b => b.brandName)
        const distinctNameList = new Set(brandNameList)
        dispatch(saveBrandOptions(Array.from(distinctNameList)))
      })
      .catch(err => console.log(err))
  },[])

  useEffect(() => {
    const loginId = localStorage && localStorage.getItem(LOCALSTORAGE.userId);
    const copyFilters = {...filterOptions};
    if (!loginId || loginId === undefined) {return}
    copyFilters.contactPerson = loginId;
    setFilterOptions(copyFilters);
  },[user])

  useEffect(() => {
    handleChangeFilterOption()
  },[filterOptions])

  return (
    <div className={styles.page_container}>
      <SearchFilter
        onChangeFilter={(option) => setFilterOptions(option)}
        filterOptions={filterOptions}
      />
      {Array.isArray(brandList) && brandList.length > 0 ? (
        loading ? (
          <div className={styles.loading}>
            <div>検索中</div>
            <CircularProgress size={30} />
          </div>
        ) : (
          <>
            <div className={`${styles.invoice_menu} ${styles.sameRow}`}>
              <div>
                <Stack
                  direction="row"
                  spacing={2}
                  className={styles.file_buttons}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ height: "33px", fontSize: "12px" }}
                    onClick={() => setOpenBatchDownload(true)}
                    disableElevation
                  >
                    請求書一括ダウンロード
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    sx={{ height: "33px", fontSize: "12px" }}
                    onClick={() => setOpenUploadFreightCost(true)}
                  >
                    運賃をアップロード
                  </Button>
                </Stack>
                <TablePagination
                  className={styles.pagination}
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={(e, page) => setPage(page)}
                  rowsPerPage={pageLimit}
                  onRowsPerPageChange={(e) =>
                    handleChangePageLimit(parseInt(e.target.value))
                  }
                  labelRowsPerPage={<span>表示件数：</span>}
                  labelDisplayedRows={({ page }) => paginationRender(page + 1)}
                  rowsPerPageOptions={[50, 100]}
                />
              </div>
            </div>
            <div className={styles.list_container}>
              {brandList &&
                brandList.map((item, index) => (
                  <CompanyInvoice
                    key={index}
                    data={item}
                    onOpenOfficeWorkFee={(brand) =>
                      handleOpenOfficeWorkFee(brand)
                    }
                    onOpenProcessFee={(brand) => handleOpenProcessFee(brand)}
                    onOpenWrappingFee={(brand) => handleOpenWrappingFee(brand)}
                    onChangeInvStatus={(id, inv) => changeInvStatus(id, inv)}
                    afterDownload={() => handleChangeFilterOption()}
                  />
                ))}
            </div>
            <div className={styles.bttm_pagination}>
              <TablePagination
                className={styles.pagination}
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(e, page) => setPage(page)}
                rowsPerPage={pageLimit}
                onRowsPerPageChange={(e) =>
                  handleChangePageLimit(parseInt(e.target.value))
                }
                labelRowsPerPage={<span>表示件数：</span>}
                labelDisplayedRows={({ page }) => paginationRender(page + 1)}
                rowsPerPageOptions={[50, 100]}
              />
            </div>
          </>
        )
      ) : loading ? (
        <div className={styles.loading}>
          <div>検索中</div>
          <CircularProgress size={30} />
        </div>
      ) : (
        <div className={styles.no_data}>
          データがありません
        </div>
      )}
      {processFeeState.openDialog ? (
        <ProcessFeeDetailsDialog
          targetBrand={processFeeState.brand}
          targetInvoices={processFeeState.invoices}
          processFeeOptions={processFeeState.options}
          onClose={() => {
            closeProcessFeeDialog();
          }}
          onCancel={() => {
            closeProcessFeeDialog();
          }}
          open={processFeeState.openDialog}
          onSave={(items) => addProcessFeeDetails(items)}
          from="brandlist"
          onAddOptions={(val) => addProcessFeeOption(val)} // addProcessFeeOption(val)}
        />
      ) : (
        <></>
      )}
      {officeWorkFeeState.openDialog ? (
        <OfficeWorkDialog
          targetBrand={officeWorkFeeState.brand}
          targetInvoices={officeWorkFeeState.invoices}
          officeWorkCategoryOptions={officeWorkFeeState.options}
          onClose={() => closeOfficeWorkFeeDialog()}
          open={officeWorkFeeState.openDialog}
          onSave={(val) => saveOfficeWorkFee(val)}
          existDates={officeWorkFeeState.dateArray}
        />
      ) : (
        <></>
      )}
      {wrappingFeeState.openDialog ? (
        <WrappingFeeDialog
          targetBrand={wrappingFeeState.brand}
          targetInvoices={wrappingFeeState.invoices}
          wrappingDeliveryOptions={wrappingFeeState.deliveryOptions}
          wrappingSizeOptions={wrappingFeeState.sizeOptions}
          onClose={() => closeWrappingFeeDialog()}
          open={wrappingFeeState.openDialog}
          onSave={(items) => {
            saveWrappingFee(items);
          }}
          existDates={wrappingFeeState.dateArray}
        />
      ) : (
        <></>
      )}
      <UploadCsvDialog
        open={openUploadFreightCost}
        onClose={() => setOpenUploadFreightCost(false)}
        onSuccess={() => {}}
      />
      <BatchDownloadDialog
        open={openBatchDownload}
        onClose={() => setOpenBatchDownload(false)}
        onSave={(pdf, excel) => downloadInvoice(pdf, excel)}
      />
      {isLoadingChangeStatus ? (
        <div className={styles.overlay}>
          <CircularProgress size={40} sx={{ color: "#F4F6FA" }} />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
